"use client";

import { useForm, useWatch } from "react-hook-form";
import {
  HiSearch,
  HiExternalLink,
  HiClipboardCopy,
  HiFilter,
  HiQuestionMarkCircle,
} from "@/components/icons";
import { FaYoutube } from "@/components/icons";
import Link from "next/link";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

import { useToast } from "@/hooks/use-toast";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import UnifiedFAQ from "@/components/UnifiedFAQ";
import { youtubeAdvancedSearchFAQ } from "@/lib/data/faq/youtube-advanced-search";

type YouTubeSearchFormValues = {
  // General
  query: string;
  exactPhrase: string;
  excludeWords: string;

  // Filters
  uploadDate: "any" | "hour" | "today" | "week" | "month" | "year";
  duration: "any" | "short" | "medium" | "long"; // short < 4, long > 20
  type: "any" | "video" | "channel" | "playlist" | "movie";
  features: string[]; // 4k, hd, sub, cc, 360, vr180, 3d, hdr, location, purchased, live
  sort: "relevance" | "date" | "viewCount" | "rating";
};

export default function YouTubeAdvancedSearchClient() {
  const { register, control } = useForm<YouTubeSearchFormValues>({
    defaultValues: {
      uploadDate: "any",
      duration: "any",
      type: "any",
      sort: "relevance",
      features: [],
    },
  });

  const values = useWatch({ control });
  const { toast } = useToast();

  const queryPreview = (() => {
    const queryParts: string[] = [];

    if (values.query) queryParts.push(values.query);
    if (values.exactPhrase) queryParts.push(`"${values.exactPhrase}"`);
    if (values.excludeWords)
      queryParts.push(
        values.excludeWords
          .split(" ")
          .map((w) => `-${w}`)
          .join(" ")
      );

    // YouTube search filters are handled via URL params (sp), not directly in query text like google site: operator.
    // However, some like "intitle:" might work on google video search, but not native YouTube.
    // YouTube's 'sp' parameter is base64 encoded protobuf. This is complex to generate client-side without a library.
    // BUT, we can use the `filters` comma separation for some things if we redirect to google video search or try to mimic common filter combos.
    // Actually, simply using the query part is what users see. The filters are hidden in the URL.
    // We can't easily generate the 'sp' param without reverse engineering it or using a library.
    // ALTERNATIVE: Use standard operators that work in search bar:
    // , short, , long, , playlist, , channel, , hd, , 4k, , creative commons
    // YouTube supports comma separated filters in search query! e.g. "query, short, 4k"

    if (values.uploadDate !== "any") queryParts.push(`, ${values.uploadDate}`);
    if (values.duration !== "any") queryParts.push(`, ${values.duration}`);
    if (values.type !== "any" && values.type !== "video") queryParts.push(`, ${values.type}`); // video is default

    if (values.features && values.features.length > 0) {
      queryParts.push(`, ${values.features.join(", ")}`);
    }

    return queryParts.join(" ");
  })();

  const fullUrl = `https://www.youtube.com/results?search_query=${encodeURIComponent(queryPreview)}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(queryPreview);
    toast({
      title: "Copied to clipboard",
      description: "Search query copied to clipboard.",
    });
  };

  return (
    <div className="container px-4 py-12 mx-auto max-w-4xl">
      <ToolPageSchema
        name="YouTube Advanced Search Generator"
        description="Find specific video clips for research with advanced filters. Filter by upload date, duration, video type, quality (HD, 4K), creative commons, and more."
        url="https://plugmonkey.xyz/tool/youtube-advanced-search/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "YouTube Advanced Search",
            url: "https://plugmonkey.xyz/tool/youtube-advanced-search/",
          },
        ]}
        faq={youtubeAdvancedSearchFAQ.map((item) => ({
          question: item.question,
          answer: item.answer,
        }))}
      />
      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-red-600/10 rounded-full text-red-600">
          <FaYoutube className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">YouTube Advanced Search Generator</h1>
          <p className="text-muted-foreground mt-1">
            Find specific video clips for research with advanced filters.
          </p>
        </div>
      </div>

      <div className="mb-8 text-muted-foreground leading-relaxed space-y-3 max-w-3xl">
        <p>
          YouTube's search is powerful, but using advanced filters requires knowing the right
          operators and syntax. This tool makes it simple—build complex searches with filters for
          upload date, duration, video quality, creative commons licensing, and more, all through a
          clean interface.
        </p>
        <p>
          Perfect for researchers, content creators, and anyone who needs to find specific types of
          videos quickly. Filter by HD or 4K quality, find videos with subtitles, search for live
          streams, or narrow down to creative commons content you can use freely. Generate your
          search query, copy it, or jump straight to YouTube with your filters applied.
        </p>
        <p>
          No more guessing at search syntax. Build your query visually, see exactly what you're
          searching for, and get better results faster.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HiSearch /> Search Terms
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="ytas-query">Search Query</Label>
                <Input id="ytas-query" {...register("query")} placeholder="tutorials" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ytas-exactPhrase">Exact Phrase</Label>
                  <Input
                    id="ytas-exactPhrase"
                    {...register("exactPhrase")}
                    placeholder="nextjs tutorial"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ytas-excludeWords">Exclude Words</Label>
                  <Input
                    id="ytas-excludeWords"
                    {...register("excludeWords")}
                    placeholder="beginner"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HiFilter /> Filters
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-3">
                  <Label htmlFor="ytas-uploadDate">Upload Date</Label>
                  <select
                    id="ytas-uploadDate"
                    {...register("uploadDate")}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="any">Anytime</option>
                    <option value="hour">Last Hour</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="year">This Year</option>
                  </select>
                </div>
                <div className="space-y-3">
                  <Label htmlFor="ytas-duration">Duration</Label>
                  <select
                    id="ytas-duration"
                    {...register("duration")}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="any">Any Duration</option>
                    <option value="short">Short (&lt; 4 min)</option>
                    <option value="medium">Medium (4-20 min)</option>
                    <option value="long">Long (&gt; 20 min)</option>
                  </select>
                </div>
                <div className="space-y-3">
                  <Label htmlFor="ytas-type">Type</Label>
                  <select
                    id="ytas-type"
                    {...register("type")}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="any">Any Type</option>
                    <option value="video">Video</option>
                    <option value="channel">Channel</option>
                    <option value="playlist">Playlist</option>
                    <option value="movie">Movie</option>
                  </select>
                </div>
              </div>

              <div className="space-y-3 border-t border-border/50 pt-4">
                {/* Group heading, not a <label>: it names the checkbox group below
                    via aria-labelledby, and a <label> with no field of its own is
                    flagged by Chrome. */}
                <span id="ytas-features-label" className="text-sm font-medium leading-none">
                  Features
                </span>
                <div
                  className="grid grid-cols-2 md:grid-cols-4 gap-3"
                  role="group"
                  aria-labelledby="ytas-features-label"
                >
                  {[
                    "hd",
                    "4k",
                    "creative commons",
                    "360",
                    "vr180",
                    "3d",
                    "hdr",
                    "live",
                    "subtitles",
                  ].map((feature) => (
                    <label key={feature} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        value={feature}
                        {...register("features")}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="capitalize">{feature}</span>
                    </label>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-6 space-y-6">
            <Card className="bg-muted/50 border-primary/20 shadow-lg">
              <CardHeader>
                <CardTitle>Generated Query</CardTitle>
                <CardDescription>Ready to use on YouTube</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-background border rounded-md p-4 font-mono text-sm break-words min-h-[60px]">
                  {queryPreview || (
                    <span className="text-muted-foreground opacity-50">
                      Start typing to generate query...
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-3">
                  <Button
                    onClick={copyToClipboard}
                    variant="outline"
                    className="w-full"
                    disabled={!queryPreview}
                  >
                    <HiClipboardCopy className="mr-2 h-4 w-4" /> Copy Query
                  </Button>
                  <Button
                    onClick={() => window.open(fullUrl, "_blank")}
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                    disabled={!queryPreview}
                  >
                    Search on <FaYoutube className="mr-2 h-4 w-4" />
                    <HiExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="bg-card border rounded-xl p-6">
              <h2 className="font-bold text-lg mb-4">Downloading Videos?</h2>
              <p className="text-muted-foreground text-sm mb-4">
                Need to save high-quality videos for your projects?{" "}
                <strong>Video Downloader Pro</strong> supports 4K, HLS, and more.
              </p>
              <Button asChild variant="secondary" className="w-full">
                <Link href="/product/video-downloader-pro/">View Extension</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <section className="mt-16 pt-12 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12 shadow-sm">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <HiQuestionMarkCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm">
                  Click on a question to view the answer.
                </p>
              </div>
            </div>
            <UnifiedFAQ
              items={youtubeAdvancedSearchFAQ.map((item) => ({
                question: item.question,
                answer: item.answer,
              }))}
              showSupportLink={false}
              className="space-y-4"
            />
          </div>
        </div>
      </section>

      <ToolsCustomDevCTA />
    </div>
  );
}
